import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";

import {
  FiActivity,
  FiClock,
  FiBarChart2
} from "react-icons/fi";

import { MdVerifiedUser, MdBlock, MdSecurity } from "react-icons/md";
import { HiUsers } from "react-icons/hi";

import {
  FiUsers,
  FiUserCheck,
  FiUserX
} from "react-icons/fi";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from "chart.js";

import { Line, Doughnut } from "react-chartjs-2";
import "../styles/AdminOverview.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export default function AdminOverview() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    avgSession: "24m",
    bounceRate: "26%",
    conversion: "3.2%"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_BASE_URL}/api/admin/dashboard-overview`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const result = await res.json();
        setData(result);

        // Calculate dynamic stats based on data
        if (result) {
          const totalUsers = result.totals.users + result.totals.solicitors;
          const activeUsers = totalUsers - result.totals.blocked;
          const avgSessionMinutes = Math.round(24 * (activeUsers / totalUsers)) || 24;
          const bounceRate = Math.round(26 * (result.totals.blocked / totalUsers)) || 26;
          const conversion = (result.thisMonth.solicitors / result.thisMonth.users * 100).toFixed(1) || 3.2;

          setStats(prev => ({
            ...prev,
            avgSession: `${avgSessionMinutes}m`,
            bounceRate: `${bounceRate}%`,
            conversion: `${conversion}%`
          }));
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="adminloading-overlay">
        <div className="adminloading-spinner"></div>
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (!data) return null;

  /* ======================
     CHART DATA CONFIGURATIONS
  ====================== */
  const lineChartData = {
    labels: data.monthlyRegistrations.slice(0, 6).map(m => m.month), // Last 6 months
    datasets: [
      {
        label: "Users",
        data: data.monthlyRegistrations.slice(0, 6).map(m => m.users),
        borderColor: "#4f46e5",
        backgroundColor: "rgba(79, 70, 229, 0.1)",
        tension: 0.4,
        fill: true,
        borderWidth: 3
      },
      {
        label: "Solicitors",
        data: data.monthlyRegistrations.slice(0, 6).map(m => m.solicitors),
        borderColor: "#10b981",
        backgroundColor: "rgba(16, 185, 129, 0.1)",
        tension: 0.4,
        fill: true,
        borderWidth: 3
      }
    ]
  };

  const doughnutData = {
    labels: ["Verified", "Suspended", "Blocked"],
    datasets: [
      {
        data: [
          Math.round(data.totals.solicitors * 0.7),
          Math.round(data.totals.solicitors * 0.2),
          Math.round(data.totals.solicitors * 0.1)
        ],
        backgroundColor: ["#10b981", "#f59e0b", "#ef4444"],
        borderWidth: 2,
        borderColor: "#ffffff"
      }
    ]
  };

  const quickStats = [
    {
      label: "Avg Session",
      value: stats.avgSession,
      icon: <FiClock />
    },
    {
      label: "Bounce Rate",
      value: stats.bounceRate,
      icon: <FiActivity />
    },
    {
      label: "Conversion",
      value: stats.conversion,
      icon: <FiBarChart2 />
    },
  ];

  return (
    <div className="admin-overview">
      {/* ======================
         HEADER
      ====================== */}
      <div className="overview-header">
        <div className="header-left">
          <h1 className="overview-title">Dashboard Overview</h1>
          <p className="overview-subtitle">Welcome back, Administrator. Here's what's happening today.</p>
        </div>
      </div>

      {/* ======================
         KPI CARDS GRID
      ====================== */}
      <div className="kpi-grid">
        <KPICard
          title="Users"
          value={data.totals.users}
          icon={<FiUsers />}
          color="blue"
          subtitle="Active accounts"
        />
        <KPICard
          title="Solicitors"
          value={data.totals.solicitors}
          icon={<FiUserCheck  />}
          color="green"
          subtitle="Verified professionals"
        />
        <KPICard
          title="Blocked Accounts"
          value={data.totals.blocked}
          icon={<FiUserX  />}
          color="red"
          subtitle="Restricted access"
        />
        <KPICard
          title="Admin Users"
          value={data.totals.admins}
          icon={<MdSecurity />}
          color="purple"
          subtitle="System administrators"
        />
      </div>

      {/* ======================
         MAIN CHARTS SECTION
      ====================== */}
      <div className="charts-grid">
        {/* LINE CHART */}
        <div className="chart-card">
          <div className="chart-header">
            <div>
              <h3>User Growth Trend</h3>
              <p>Registration analytics</p>
            </div>
          </div>
          <div className="chart-container">
            <Line
              data={lineChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0,0,0,0.05)'
                    }
                  },
                  x: {
                    grid: {
                      display: false
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        {/* DOUGHNUT CHART */}
        <div className="chart-card">
          <div className="chart-header">
            <h3>Verification Status</h3>
            <p>Solicitor verification distribution</p>
          </div>
          <div className="doughnut-container">
            <Doughnut
              data={doughnutData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                cutout: '70%',
                plugins: {
                  legend: {
                    position: 'bottom'
                  }
                }
              }}
            />
            <div className="doughnut-center">
              <span className="center-value">{data.totals.solicitors}</span>
              <span className="center-label">Solicitors</span>
            </div>
          </div>
        </div>
      </div>

      {/* ======================
         RECENT ACTIVITY TABLE
      ====================== */}
      <div className="recent-activity">
        <div className="activity-header">
          <h3>Recent User Activity</h3>
        </div>
        <div className="activity-table">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th>Status</th>
                <th>Date Joined</th>
              </tr>
            </thead>
            <tbody>
              {data.recentActivity?.map((user, index) => (
                <tr key={index}>
                  <td>
                    <div className="user-cell">
                      <div className="user-avataroverview">
  {user.role === "USER" && user.userProfile?.photo ? (
    <img
      src={user.userProfile.photo}
      alt={user.name}
      className="overview-avatar-img"
    />
  ) : user.role === "SOLICITOR" && user.profile?.photo ? (
    <img
      src={user.profile.photo}
      alt={user.name}
      className="overview-avatar-img"
    />
  ) : (
    <div className="avatar-fallback">
      {user.name?.charAt(0)?.toUpperCase()}
    </div>
  )}
</div>

                      <div>
                        <div className="user-name">{user.name}</div>
                        <div className="user-email">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={`role-badge ${user.role?.toLowerCase()}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${user.status?.toLowerCase()}`}>
                      {user.status}
                    </span>
                  </td>
                  <td>
                    {new Date(user.createdAt).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>


    </div>

  );
}



/* ======================
   KPI CARD COMPONENT (Simplified - No Trend)
====================== */
function KPICard({ title, value, icon, color, subtitle }) {
  return (
    <div className={`overview-stat-card ${color}`}>
      <div className={`overview-stat-icon ${color}`}>
        {icon}
      </div>

      <div className="overview-stat-content">
        <h3 className="overview-stat-value">
          {value.toLocaleString()}
        </h3>
        <p className="overview-stat-title">{title}</p>
        <span className="overview-stat-subtitle">{subtitle}</span>
      </div>
    </div>
  );
}
